import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import * as commissionActions from '~actions/pms-actions/properties/commission';
import { Hosts } from '~helpers/environments';
import { getItem } from '~helpers/storage';
import CommissionFormModal from '~pages/pms-dashboard/properties/commission/commission-form-modal';
import CommissionList from '~pages/pms-dashboard/properties/commission/commission-list';
import CommissionEmpty from '~pages/pms-dashboard/properties/commission/commission-empty';
import CreateCommission from '~pages/pms-dashboard/properties/commission/create-commission';
import LeaveAlert from '~components/leave-alert';
import Header from '~components/property-header';
import { communicationStatus, platformEntity, entityAction } from '~constants';
import { isInternalRole } from '~helpers/auth';
import authControl from '~components/auth-control';
import generatePath from '~settings/routing';
import { trackViewPropertyCommissionPage } from '../track';
import { commissionLevel } from '~constants/commission';

const mapStateToProps = state => {
  const data = state.pmsReducer.dashboard.commissionReducer.toJS();

  return {
    property: data.propertyCommissionTiers,
    getPropertyStatus: data.communication.propertyCommissionTiers.status,
    propertyCommissionTiers: data.propertyCommissionTiers?.allCommissionTiers?.propertyLevel ?? [],
    landlordCommissionTiers: data.propertyCommissionTiers?.allCommissionTiers?.landlordLevel ?? [],
    platformCommissionTiers: data.propertyCommissionTiers?.allCommissionTiers?.platformLevel ?? [],
    createStatus: data.communication.create.status,
    updateStatus: data.communication.update.status,
    deleteStatus: data.communication.delete.status,
  };
};

const mapDispatchToProps = dispatch => ({
  getPropertyCommissionTiers: (id, successCallback) => {
    dispatch(commissionActions.getPropertyCommissionTiers(id, successCallback));
  },
});

@connect(mapStateToProps, mapDispatchToProps)
@withTranslation()
@authControl(platformEntity.COMMISSION_COMISSION_TIERS, entityAction.READ, props =>
  generatePath('property.homepage', { propertySlug: props.match.params.propertySlug }),
)
export default class PropertyCommission extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      commissionCategory: null,
      modalType: 'addNew',
      correlationId: null,
      showCommissionModal: false,
    };
  }

  componentDidMount() {
    this.props.getPropertyCommissionTiers(
      decodeURIComponent(this.props.match.params.propertySlug),
      () => {
        if (!this.props.property) {
          this.props.history.push(generatePath('properties', {}));
        }

        trackViewPropertyCommissionPage(this.props.property);
      },
    );
  }

  componentDidUpdate(prevProps) {
    if (
      (this.props.createStatus === communicationStatus.IDLE &&
        prevProps.createStatus === communicationStatus.FETCHING) ||
      (this.props.updateStatus === communicationStatus.IDLE &&
        prevProps.updateStatus === communicationStatus.FETCHING) ||
      (this.props.deleteStatus === communicationStatus.IDLE &&
        prevProps.deleteStatus === communicationStatus.FETCHING)
    ) {
      this.props.getPropertyCommissionTiers(
        decodeURIComponent(this.props.match.params.propertySlug),
      );
    }
  }

  generatePropertyUrl = property => {
    const path = `${
      property.city && property.city.country && property.city.country.slug
        ? property.city.country.slug
        : ''
    }/${property.city && property.city.slug ? property.city.slug : ''}/p/${property.slug}`;

    return `${Hosts.propertyUrl}${path}`;
  };

  getFilters = () => {
    const filters = getItem('cms_properties_list_filters');
    const result = {};
    if (filters) {
      Object.keys(filters).map(key => {
        if (filters[key]) {
          result[key] = filters[key];
        }
        return true;
      });
    }
    return result;
  };

  checkcommissionCategory = type => {
    this.setState({
      commissionCategory: type,
    });
  };

  createCommission = () => {
    this.setState({
      showCommissionModal: !this.state.showCommissionModal,
    });
  };

  openModal = (type, commissionTier) => {
    this.setState({
      showCommissionModal: true,
      modalType: type,
      correlationId: commissionTier ? commissionTier.id : null,
      commissionCategory:
        (type === 'addNew' || type === 'copy') && commissionTier ? commissionTier.category : null,
    });
  };

  render() {
    const {
      t,
      property,
      getPropertyStatus,
      propertyCommissionTiers,
      platformCommissionTiers,
      landlordCommissionTiers,
    } = this.props;

    return (
      <div className="property-commission">
        <LeaveAlert history={this.props.history} t={t} when={this.state.showCommissionModal} />
        <Choose>
          <When condition={getPropertyStatus !== communicationStatus.IDLE}>
            <div className="property-edit__loading">
              <Spin />
            </div>
          </When>
          <Otherwise>
            <Header t={t} property={property} type="commission" />
            <div className="property-commission__page">
              <If
                condition={
                  propertyCommissionTiers?.length === 0 &&
                  landlordCommissionTiers?.length === 0 &&
                  platformCommissionTiers?.length === 0
                }
              >
                <If condition={isInternalRole()}>
                  <div className="property-commission__content-create">
                    <h3 className="property-commission__content-title">
                      {t('cms:cms.property.commission.create.title.text')}
                    </h3>
                    <CreateCommission
                      t={t}
                      property={property}
                      commissionCategory={this.state.commissionCategory}
                      oncommissionCategory={this.checkcommissionCategory}
                      createCommission={this.createCommission}
                    />
                  </div>
                </If>
                <If condition={!isInternalRole()}>
                  <CommissionEmpty t={t} />
                </If>
              </If>
              <If condition={propertyCommissionTiers?.length > 0}>
                <h3 className="property-commission__section-title">
                  {t('cms:cms.property.commission.section.property_level.title')}
                </h3>

                <CommissionList
                  commissionLevel={commissionLevel.PROPERTY}
                  openModal={this.openModal}
                  bookingJourney={property.bookingJourney}
                  commissionTier={propertyCommissionTiers}
                  t={t}
                  currency={property.currency}
                />
              </If>
              <If condition={landlordCommissionTiers?.length > 0}>
                <h3 className="property-commission__section-title">{'Landlord Level'}</h3>

                <CommissionList
                  commissionLevel={commissionLevel.LANDLORD}
                  openModal={this.openModal}
                  bookingJourney={property.bookingJourney}
                  commissionTier={landlordCommissionTiers}
                  t={t}
                  currency={property.currency}
                />

                <div className="property-commission__section-tooltip">
                  <ExclamationCircleOutlined style={{ fontWeight: 'bold' }} />
                  <span>{t('cms:cms.property.commission.section.landlord_level.tooltip')}</span>
                </div>
              </If>
              <If condition={platformCommissionTiers?.length > 0}>
                <h3 className="property-commission__section-title">{'Platform Level'}</h3>
                <CommissionList
                  commissionLevel={commissionLevel.PLATFORM}
                  openModal={this.openModal}
                  bookingJourney={property.bookingJourney}
                  commissionTier={platformCommissionTiers}
                  t={t}
                  currency={property.currency}
                />
              </If>

              <If condition={this.state.showCommissionModal}>
                <CommissionFormModal
                  activeModal
                  t={t}
                  property={property}
                  commissionTiers={[...propertyCommissionTiers, ...landlordCommissionTiers]}
                  onClose={this.createCommission}
                  commissionCategory={this.state.commissionCategory}
                  oncommissionCategory={this.checkcommissionCategory}
                  modalType={this.state.modalType.toString()}
                  correlationId={this.state.correlationId}
                  openModal={this.openModal}
                />
              </If>
            </div>
          </Otherwise>
        </Choose>
      </div>
    );
  }
}

PropertyCommission.propTypes = {
  t: PropTypes.func,
  match: PropTypes.object,
  property: PropTypes.object,
  getPropertyCommissionTiers: PropTypes.func,
  history: PropTypes.object,
  getPropertyStatus: PropTypes.string,
  commissionTiers: PropTypes.array,
  createStatus: PropTypes.string,
  updateStatus: PropTypes.string,
  deleteStatus: PropTypes.string,
};

PropertyCommission.defaultProps = {
  match: {},
  history: {},
  property: {},
  t: () => {},
  getPropertyCommissionTiers: () => {},
  getPropertyStatus: '',
  commissionTiers: [],
  createStatus: '',
  updateStatus: '',
  deleteStatus: '',
};
